/**
 * BankConnectionScreen
 * Bank connection flow using Open Banking (Bridge or Powens)
 * Opens OAuth in system browser for security, handles callback via deep linking
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
  Platform,
  Alert,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import * as ExpoLinking from 'expo-linking';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import { bankSyncApi } from '../api/endpoints/bankSync';
import { openWhatsAppSupport } from '../utils/whatsapp';
import PaywallModal from '../components/common/PaywallModal';
import logger from '../utils/logger';

type RouteParams = {
  BankConnection: {
    mode?: 'connect' | 'reconnect';
    connectionId?: string;
    redirectUrl?: string;
    // Callback params from deep link or URL redirect
    externalConnectionId?: string;
    // Powens callback param
    connection_id?: string;
    // Bridge callback params
    item_id?: string;
    success?: string;
    source?: string;
  };
};

type ConnectionState =
  | 'ready'
  | 'loading'
  | 'browser'
  | 'processing'
  | 'success'
  | 'premium_required'
  | 'error';

export default function BankConnectionScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<RouteProp<RouteParams, 'BankConnection'>>();

  const {
    mode = 'connect',
    connectionId: existingConnectionId,
    redirectUrl: existingRedirectUrl,
    externalConnectionId,
    connection_id: urlConnectionId, // Powens callback param
    item_id: bridgeItemId, // Bridge callback param
  } = route.params || {};

  // Use either externalConnectionId, connection_id (Powens), or item_id (Bridge)
  const externalId = externalConnectionId || urlConnectionId || bridgeItemId;

  const [state, setState] = useState<ConnectionState>(externalId ? 'processing' : 'ready');
  const [connectionId, setConnectionId] = useState<string | null>(existingConnectionId || null);
  const [error, setError] = useState<string | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);

  // Handle callback from deep link or URL redirect
  useEffect(() => {
    if (externalId) {
      // We have the external ID (Powens connection_id or Bridge item_id), process the callback
      handleCallback(externalId);
    }
  }, [externalId]);

  const handleCallback = async (callbackExternalId: string) => {
    try {
      setState('processing');

      // Get Wealfi connectionId from state or storage
      let wealfiConnectionId = connectionId;

      if (!wealfiConnectionId) {
        // Try to retrieve from storage (when returning from browser)
        if (Platform.OS === 'web') {
          wealfiConnectionId = localStorage.getItem('bank_callback_wealfi_connection_id');
          localStorage.removeItem('bank_callback_wealfi_connection_id');
        } else {
          wealfiConnectionId = await AsyncStorage.getItem('bank_callback_wealfi_connection_id');
          await AsyncStorage.removeItem('bank_callback_wealfi_connection_id');
        }
      }

      if (!wealfiConnectionId) {
        throw new Error('connectionId local manquant - veuillez recommencer la connexion');
      }

      logger.log('[BankConnection] Completing callback with Wealfi ID:', wealfiConnectionId, 'External ID:', callbackExternalId);

      // Complete the callback (works for both Powens connection_id and Bridge item_id)
      await bankSyncApi.completeCallback(wealfiConnectionId, callbackExternalId);

      setState('success');

      // Wait a moment then navigate back to chat and auto-trigger the scanner
      setTimeout(() => {
        navigation.navigate('MainContent', {
          initialRoute: 'Chats',
          bankConnectionComplete: true,
          connectionId: connectionId,
          triggerScanner: true,
          scannerTimestamp: Date.now(),
        });
      }, 2000);
    } catch (err: any) {
      logger.error('[BankConnection] Error processing callback:', err);
      setError(err.message || 'Erreur lors de la finalisation de la connexion.');
      setState('error');
    }
  };

  const handleConnect = async () => {
    try {
      setState('loading');

      let url: string;
      let connId: string | null = connectionId;

      if (mode === 'reconnect' && existingRedirectUrl) {
        // Use provided reconnect URL
        url = existingRedirectUrl;
      } else {
        // Initiate new connection
        const response = await bankSyncApi.initiateConnection();
        connId = response.connection_id;
        setConnectionId(connId);
        url = response.redirect_url;
      }

      // Store connectionId for when we return from browser (web platform)
      if (Platform.OS === 'web' && connId) {
        localStorage.setItem('bank_callback_wealfi_connection_id', connId);
        logger.log('[BankConnection] Stored Wealfi connection ID for callback:', connId);
      } else if (connId) {
        await AsyncStorage.setItem('bank_callback_wealfi_connection_id', connId);
      }

      setState('browser');

      // Open in system browser
      const canOpen = await Linking.canOpenURL(url);
      if (canOpen) {
        await Linking.openURL(url);
      } else {
        throw new Error('Impossible d\'ouvrir le navigateur');
      }
    } catch (err: any) {
      logger.error('[BankConnection] Error initiating connection:', err);

      if (bankSyncApi.isPremiumRequiredError(err)) {
        setState('premium_required');
      } else {
        setError(err.message || 'Erreur lors de la connexion. Veuillez reessayer.');
        setState('error');
      }
    }
  };

  const handleClose = () => {
    navigation.goBack();
  };

  const handleRetry = () => {
    setError(null);
    setState('ready');
  };

  // Render ready state
  if (state === 'ready') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Icon name="close" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Connexion bancaire</Text>
          <View style={styles.placeholder} />
        </View>

        <View style={styles.content}>
          <View style={styles.iconContainer}>
            <Icon name="bank-transfer" size={64} color={colors.primary} />
          </View>

          <Text style={styles.title}>Connectez votre banque</Text>
          <Text style={styles.description}>
            Vous allez être redirigé vers un service sécurisé pour connecter votre banque.
          </Text>

          <View style={styles.securityInfo}>
            <View style={styles.securityItem}>
              <Icon name="shield-check" size={20} color={colors.success} />
              <Text style={styles.securityText}>Connexion chiffrée SSL/TLS</Text>
            </View>
            <View style={styles.securityItem}>
              <Icon name="lock" size={20} color={colors.success} />
              <Text style={styles.securityText}>Tes identifiants restent chez ta banque</Text>
            </View>
            <View style={styles.securityItem}>
              <Icon name="certificate" size={20} color={colors.success} />
              <Text style={styles.securityText}>Service agréé par la Banque de France</Text>
            </View>
          </View>

          <TouchableOpacity style={styles.connectButton} onPress={handleConnect}>
            <Icon name="open-in-new" size={20} color="#FFFFFF" />
            <Text style={styles.connectButtonText}>Continuer</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Render loading state
  if (state === 'loading') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Icon name="close" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Connexion bancaire</Text>
          <View style={styles.placeholder} />
        </View>
        <View style={styles.centerContent}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Preparation de la connexion...</Text>
        </View>
      </SafeAreaView>
    );
  }

  // Render browser state (user is in external browser)
  if (state === 'browser') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Icon name="close" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Connexion en cours</Text>
          <View style={styles.placeholder} />
        </View>
        <View style={styles.centerContent}>
          <View style={styles.browserIcon}>
            <Icon name="open-in-app" size={48} color={colors.primary} />
          </View>
          <Text style={styles.browserTitle}>Finalisez dans votre navigateur</Text>
          <Text style={styles.browserText}>
            Une fois connecte a votre banque, vous serez automatiquement redirige vers l'application.
          </Text>
          <TouchableOpacity style={styles.waitingButton} onPress={handleConnect}>
            <Icon name="refresh" size={18} color={colors.primary} />
            <Text style={styles.waitingButtonText}>Rouvrir le navigateur</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Render processing state
  if (state === 'processing') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <View style={styles.placeholder} />
          <Text style={styles.headerTitle}>Connexion en cours</Text>
          <View style={styles.placeholder} />
        </View>
        <View style={styles.centerContent}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Finalisation de la connexion...</Text>
          <Text style={styles.subText}>
            Veuillez patienter pendant que nous configurons votre compte.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Render success state
  if (state === 'success') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centerContent}>
          <View style={styles.successIcon}>
            <Icon name="check-circle" size={64} color={colors.success} />
          </View>
          <Text style={styles.successTitle}>Compte connecte !</Text>
          <Text style={styles.successText}>
            Vos transactions seront importees automatiquement.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Render premium required state
  if (state === 'premium_required') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Icon name="close" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Connexion bancaire</Text>
          <View style={styles.placeholder} />
        </View>
        <View style={styles.centerContent}>
          <View style={styles.premiumIcon}>
            <Icon name="crown" size={64} color={colors.warning} />
          </View>
          <Text style={styles.premiumTitle}>Fonctionnalité Premium</Text>
          <Text style={styles.premiumText}>
            La synchronisation bancaire automatique est réservée aux membres Premium. Passe à Premium pour connecter tes comptes bancaires et importer tes transactions automatiquement.
          </Text>
          <TouchableOpacity
            style={styles.premiumButton}
            onPress={() => setShowPaywall(true)}
          >
            <Icon name="crown" size={20} color="#FFFFFF" />
            <Text style={styles.premiumButtonText}>Passer Premium</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.cancelButton} onPress={handleClose}>
            <Text style={styles.cancelButtonText}>Plus tard</Text>
          </TouchableOpacity>
        </View>
        <PaywallModal
          visible={showPaywall}
          onClose={() => setShowPaywall(false)}
          trigger="feature"
        />
      </SafeAreaView>
    );
  }

  // Render error state
  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
          <Icon name="close" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Erreur</Text>
        <View style={styles.placeholder} />
      </View>
      <View style={styles.centerContent}>
        <View style={styles.errorIcon}>
          <Icon name="alert-circle" size={64} color={colors.error} />
        </View>
        <Text style={styles.errorTitle}>Service temporairement indisponible</Text>
        <Text style={styles.errorText}>
          Le service de connexion bancaire est momentanément indisponible. Réessaie dans quelques minutes ou contacte notre support.
        </Text>
        <View style={styles.errorActions}>
          <TouchableOpacity style={styles.retryButton} onPress={handleRetry}>
            <Icon name="refresh" size={20} color="#FFFFFF" />
            <Text style={styles.retryButtonText}>Réessayer</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.whatsappButton}
            onPress={() => openWhatsAppSupport('Erreur connexion bancaire')}
          >
            <Icon name="whatsapp" size={20} color="#FFFFFF" />
            <Text style={styles.whatsappButtonText}>Contacter le support</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.cancelButton} onPress={handleClose}>
            <Text style={styles.cancelButtonText}>Annuler</Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  closeButton: {
    padding: spacing.xs,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
  },
  placeholder: {
    width: 32,
  },
  content: {
    flex: 1,
    padding: spacing.xl,
    alignItems: 'center',
  },
  iconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  description: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
    lineHeight: 22,
  },
  securityInfo: {
    alignSelf: 'stretch',
    backgroundColor: colors.success + '08',
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.xl,
  },
  securityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  securityText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  connectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: 12,
    width: '100%',
  },
  connectButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold as '700',
    color: '#FFFFFF',
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  subText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: spacing.sm,
    textAlign: 'center',
  },
  browserIcon: {
    marginBottom: spacing.md,
  },
  browserTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  browserText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
    lineHeight: 22,
  },
  waitingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary + '10',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: 12,
  },
  waitingButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium as '500',
    color: colors.primary,
  },
  successIcon: {
    marginBottom: spacing.md,
  },
  successTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  successText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  premiumIcon: {
    marginBottom: spacing.md,
  },
  premiumTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  premiumText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
    lineHeight: 22,
  },
  premiumButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.warning,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: 12,
    width: '100%',
    maxWidth: 300,
    marginBottom: spacing.sm,
  },
  premiumButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold as '700',
    color: '#FFFFFF',
  },
  errorIcon: {
    marginBottom: spacing.md,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  errorText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  errorActions: {
    gap: spacing.md,
    width: '100%',
    maxWidth: 300,
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    borderRadius: 12,
  },
  retryButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold as '700',
    color: '#FFFFFF',
  },
  whatsappButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: '#25D366',
    paddingVertical: spacing.md,
    borderRadius: 12,
  },
  whatsappButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold as '700',
    color: '#FFFFFF',
  },
  cancelButton: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
});
